import { css } from "@emotion/react";
import { FormApi } from "final-form";
import { get, isNil, noop } from "lodash";
import { FC, useCallback, useState } from "react";
import { Field, Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";

import { Close } from "@livechat/design-system-icons";
import { Alert, FormField, Input } from "@livechat/design-system-react-components";

import { ErrorBoundary } from "components/error-boundary";
import { RememberUnmountedFields } from "components/final-form/RememberUnmountedFields";
import { FixedSaveBar, SaveBarMode } from "components/form";
import { Grid, ViewStack } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { TeamsSelect } from "components/teams/team-multi-select/TeamsSelect";
import { EMPTY_EDITOR_STATE } from "constants/rich-text";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { useToggle } from "hooks/use-toggle";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import {
  IAppearance,
  IAppearancePrimitiveItem,
  IAppearanceRichTextObjectItem,
} from "store/entities/application-settings/interfaces";
import {
  selectDefaultTemplate,
  selectDefaultVisualTemplate,
  selectDefaultVisualTemplateAppearance,
} from "store/entities/application-settings/selectors";
import { EmailTemplateActions } from "store/entities/email-template/actions";
import { EmailTemplateTypeEnum, IEmailTemplate } from "store/entities/email-template/interfaces";
import {
  IWithEmailTemplatesState,
  selectEmailTemplateById,
  selectEmailTemplatesList,
} from "store/entities/email-template/selectors";
import { selectDefaultTemplateId } from "store/entities/license/selectors";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { areSimilar } from "utils/array";
import { compose, maxLength, required } from "utils/validators";

import FullPageViewContainer from "../components/FullPageViewContainer";
import { SettingsRoutes } from "../constants";
import { PageWrapper } from "../styled";

import { HideHelpDeskSwitch } from "./HideHelpDeskSwitch";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import TemplateComposerContent from "./TemplateComposerContent";
import { TemplatePublishModal } from "./TemplatePublishModal";
import { VisualBuilderControls } from "./VisualBuilderControls";
import { ContentType } from "./constants";
import { isHTMLValid, isPlainTextValid } from "./helpers";

const EMPTY_FOOTER = {
  type: "richTextObj",
  value: {
    html: EMPTY_EDITOR_STATE,
  },
};

const Component: FC = () => {
  const [transactionID, setTransactionID] = useState<string | undefined>();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const defaultTemplateId = useSelector(selectDefaultTemplateId);
  const savedTemplates = useSelector(selectEmailTemplatesList);
  const templateId = id;
  const { pathname } = useLocation();
  const [isModalOpen, toggleModal] = useToggle(false);
  const addTemplate = pathname.startsWith(`${SettingsRoutes.EmailTemplates}/add`);
  const duplicateTemplate = pathname.startsWith(`${SettingsRoutes.EmailTemplates}/duplicate`);
  const isDefault = defaultTemplateId === id;
  const selectedTemplate = useSelector(state => selectEmailTemplateById(state as IWithEmailTemplatesState, templateId));
  const openVisualBuilder =
    pathname.includes("/visualBuilder") || selectedTemplate?.type === EmailTemplateTypeEnum.VISUAL;
  const defaultTemplate = useSelector(selectDefaultTemplate) as IEmailTemplate;
  const defaultVisualTemplate = useSelector(selectDefaultVisualTemplate) as IEmailTemplate;
  const [isDiscardChangesModalOpen, toggleDiscardChangesModal] = useToggle(false);
  const appearance = useSelector(selectDefaultVisualTemplateAppearance);
  const savedTemplatesNumber = savedTemplates.length;
  const template: IEmailTemplate = addTemplate
    ? {
        ...(openVisualBuilder ? defaultVisualTemplate : defaultTemplate),
        id: "",
        name: "",
      }
    : {
        ...(openVisualBuilder
          ? {
              ...selectedTemplate,
              content: {
                ...selectedTemplate.content,
                html: defaultVisualTemplate.content.html,
                appearance: {
                  ...selectedTemplate.content.appearance,
                  appearanceFooterTextColor1: selectedTemplate.content.appearance?.appearanceFooterTextColor1
                    ? selectedTemplate.content.appearance.appearanceFooterTextColor1
                    : appearance!.appearanceFooterTextColor1, // Assuming that it's always defined
                  appearanceFooterLinkColor1: selectedTemplate.content.appearance?.appearanceFooterLinkColor1
                    ? selectedTemplate.content.appearance.appearanceFooterLinkColor1
                    : appearance!.appearanceFooterLinkColor1, // Assuming that it's always defined
                },
              },
            }
          : selectedTemplate),
      };
  const permission = useSelector(selectLoggedInAgentPermission);
  const isOwner = permission === Role.Owner;
  const initialTeamIDs = useSelector(selectTeamsAlphabetically) // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((team: any) => team.templateId === template?.id) // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((team: any) => team.id);
  const history = useHistory();
  let finalForm: FormApi;
  let templateName = template.name;
  const isEmbeddedInIframe = isInIframe();

  if (duplicateTemplate) {
    templateName = `Copy of ${template.name}`;
  }

  if (addTemplate) {
    templateName = `Email template ${savedTemplatesNumber + 1}`;
  }

  const disabled = !isOwner;

  const [templates, setTemplateContent] = useState({
    [ContentType.HTML]: template.content.html,
    [ContentType.PlainText]: template.content.text,
  });

  const onContentChange = useCallback(
    (contentType: ContentType, content: string) => {
      setTemplateContent({ ...templates, [contentType]: content });
    },
    [templates]
  );

  const handleReset = useCallback(() => {
    setTemplateContent({ [ContentType.HTML]: template.content.html, [ContentType.PlainText]: template.content.text });
    finalForm.setConfig("keepDirtyOnReinitialize", false);
    finalForm.reset();
    finalForm.setConfig("keepDirtyOnReinitialize", true);
    trackEngagement("Email template: resetted", EventPlace.Settings);
    history.push(SettingsRoutes.EmailTemplates);
    // @ts-expect-error TS(2454)
  }, [template.content.html, template.content.text, finalForm, history]);

  const updateTemplate = useCallback(
    ({
      content,
      name,
      teamIDs,
      type,
    }: {
      content: { text: string; html: string; appearance?: IAppearance };
      name: string;
      teamIDs: string[];
      type?: EmailTemplateTypeEnum;
    }) => {
      if (addTemplate || duplicateTemplate) {
        dispatch(EmailTemplateActions.create({ value: { content, name, teamIDs, type, transactionID } }));
      } else {
        dispatch(
          EmailTemplateActions.update({ value: { id: template.id, content, name, teamIDs, type, transactionID } })
        );
      }
      let emailTemplateEventType = `${openVisualBuilder ? "Visual" : "HTML"} template editor: Template changes saved`;
      if (addTemplate) {
        emailTemplateEventType = `${openVisualBuilder ? "Visual" : "HTML"} template editor: Template created`;
      } else if (duplicateTemplate) {
        emailTemplateEventType = "Email templates: Duplicate template";
      }

      trackEngagement(emailTemplateEventType, EventPlace.Settings, {
        htmlChanged: template.content.html !== templates[ContentType.HTML],
        plainTextChanged: template.content.text !== templates[ContentType.PlainText],
        teamsChanged: !areSimilar(initialTeamIDs, teamIDs),
        initialTemplate: template?.createdByType === "system",
      });
    },
    [
      addTemplate,
      duplicateTemplate,
      openVisualBuilder,
      template.content.html,
      template.content.text,
      template?.createdByType,
      template.id,
      templates,
      initialTeamIDs,
      dispatch,
      transactionID,
    ]
  );

  const sendTest = useCallback(
    (emails: string[]) => {
      const type = openVisualBuilder ? EmailTemplateTypeEnum.VISUAL : EmailTemplateTypeEnum.HTML;
      const { appearance } = finalForm.getState().values;
      dispatch(
        EmailTemplateActions.test(
          emails,
          {
            html: templates[ContentType.HTML],
            text: templates[ContentType.PlainText],
            appearance: openVisualBuilder ? appearance : undefined,
          },
          type
        )
      );
      trackEngagement(
        `${openVisualBuilder ? "Visual" : "HTML"} template editor: Test email sent`,
        EventPlace.Settings,
        {
          emailsCount: emails.length,
          htmlChanged: template.content.html !== templates[ContentType.HTML],
          plainTextChanged: template.content.text !== templates[ContentType.PlainText],
        }
      );
    },
    // @ts-expect-error TS(2454)
    [dispatch, finalForm, openVisualBuilder, template.content.html, template.content.text, templates]
  );

  const handleSave = useCallback(
    (formValues: { templateTitle?: string; teamIDs?: string[]; appearance?: IAppearance }) => {
      const { templateTitle: name, teamIDs, appearance } = formValues;
      if (!name || !teamIDs) {
        return;
      }
      updateTemplate({
        content: {
          text: templates[ContentType.PlainText],
          html: templates[ContentType.HTML],
          appearance: openVisualBuilder ? appearance : undefined,
        },
        name,
        teamIDs,
        type: openVisualBuilder ? EmailTemplateTypeEnum.VISUAL : undefined,
      });
    },
    [openVisualBuilder, templates, updateTemplate]
  );

  const allContentValid = openVisualBuilder
    ? true
    : isPlainTextValid(templates[ContentType.PlainText]) && isHTMLValid(templates[ContentType.HTML]);

  const hasChanged = Object.entries(templates).some(([contentType, content]) => {
    const rawTemplate = contentType === ContentType.HTML ? template.content.html : template.content.text;
    return content !== rawTemplate;
  });

  function handleClickBack(): void {
    if (hasChanged || !finalForm?.getState()?.pristine) {
      toggleDiscardChangesModal();
    } else {
      history.push(SettingsRoutes.EmailTemplates);
    }
  }

  const getValue = (key: string): IAppearancePrimitiveItem | IAppearanceRichTextObjectItem | undefined =>
    addTemplate && !isNil(appearance) ? get(appearance, key) : get(template?.content?.appearance, key);

  const initialAppearance = {
    appearanceBodyBgColor1: getValue("appearanceBodyBgColor1"),
    appearanceContentBgColor1: getValue("appearanceContentBgColor1"),
    appearanceTextColor1: getValue("appearanceTextColor1"),
    appearanceLinkColor1: getValue("appearanceLinkColor1"),
    appearanceFooterTextColor1: getValue("appearanceFooterTextColor1"),
    appearanceFooterLinkColor1: getValue("appearanceFooterLinkColor1"),
    contentShowPreviousMessages: getValue("contentShowPreviousMessages"),
    appearanceShowLogo: getValue("appearanceShowLogo"),
    appearanceLogoUrl: getValue("appearanceLogoUrl"),
    footerShowFooter: getValue("footerShowFooter"),
    footerBody: getValue("footerBody") === null ? EMPTY_FOOTER : getValue("footerBody"),
    contentBody: getValue("contentBody"),
  };

  return (
    <FullPageViewContainer
      title={`${addTemplate ? "New" : "Edit"} email template`}
      onBack={handleClickBack}
      backButtonIcon={isEmbeddedInIframe ? Close : undefined}
    >
      <PageWrapper>
        <Form
          onSubmit={noop}
          initialValues={{
            templateTitle: templateName,
            teamIDs: initialTeamIDs,
            appearance: openVisualBuilder ? initialAppearance : undefined,
          }}
          keepDirtyOnReinitialize
        >
          {({ valid, values, pristine, form }) => {
            finalForm = form;

            return (
              <Grid relativeWidths={[1, 3]} minWidths={[460, 0]}>
                <ViewStack
                  css={css({
                    overflow: "auto",
                    height: hasChanged || !pristine ? "calc(100% - 100px)" : "100%",
                  })}
                >
                  <Flex padding="20px" column>
                    {isDefault && !duplicateTemplate && (
                      <Block marginBottom="20px">
                        <Alert>
                          This is the default email template and will be used in the response process for:
                          <ul css={css({ marginLeft: "20px", listStyle: "disc" })}>
                            <li>All teams without an assigned email template</li>
                            <li>Teams selected in the &ldquo;Teams assigned to email template&ldquo; field</li>
                          </ul>
                        </Alert>
                      </Block>
                    )}
                    <Field
                      validate={compose(
                        required("Name is required"),
                        maxLength("Name is too long (max 64 characters)", 64)
                      )}
                      id="email-template-name"
                      name="templateTitle"
                    >
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {({ input, meta }: any) => (
                        <FormField labelText="Email template name" error={meta.touched && meta.error}>
                          <Input
                            placeholder="Name the email template"
                            data-testid="email-template-name"
                            error={meta.touched && meta.error}
                            data-1p-ignore
                            {...input}
                          />
                        </FormField>
                      )}
                    </Field>
                    <Field id="custom-field-team-select" name="teamIDs" labelText="Assign to teams" size="big">
                      {({ input: { onChange }, meta, id }) => (
                        <TeamsSelect
                          labelText="Teams assigned to email template"
                          placeholder="Select who will use the email template"
                          filterValues={values.teamIDs}
                          onChange={onChange}
                          meta={meta}
                          id={id}
                        />
                      )}
                    </Field>
                    {openVisualBuilder ? (
                      <VisualBuilderControls
                        values={values.appearance}
                        templateID={templateId}
                        setTransactionID={setTransactionID}
                      />
                    ) : (
                      <HideHelpDeskSwitch isVisualBuilder={openVisualBuilder} />
                    )}
                    <FixedSaveBar
                      embeddedInIframe={isEmbeddedInIframe}
                      mode={
                        hasChanged || !pristine || duplicateTemplate || addTemplate
                          ? SaveBarMode.OfferSave
                          : SaveBarMode.Hidden
                      }
                      saveDisabled={!allContentValid || !valid}
                      onSave={toggleModal}
                      onCancel={toggleDiscardChangesModal}
                      customSaveLabel={`${addTemplate || duplicateTemplate ? "Create" : "Update"} email template`}
                    />
                    {isModalOpen && (
                      <TemplatePublishModal
                        onClose={toggleModal}
                        onSave={() => handleSave(values)}
                        mode={addTemplate || duplicateTemplate ? "add" : "edit"}
                      />
                    )}
                  </Flex>
                </ViewStack>
                <ViewStack>
                  <Flex padding={openVisualBuilder ? "8px 12px 12px" : "12px"}>
                    <TemplateComposerContent
                      isVisualTemplate={openVisualBuilder}
                      sendTest={sendTest}
                      disabled={disabled}
                      templates={templates}
                      onContentChange={onContentChange}
                      hasChanged={hasChanged || !pristine}
                      appearance={values.appearance}
                    />
                  </Flex>
                </ViewStack>
                <RememberUnmountedFields />
              </Grid>
            );
          }}
        </Form>
      </PageWrapper>
      {isDiscardChangesModalOpen && (
        <ConfirmActionModal
          heading="Changes not saved"
          onClose={toggleDiscardChangesModal}
          primaryAction={{
            label: "Back to editing",
            onClick: toggleDiscardChangesModal,
            actionType: "primary",
          }}
          secondaryAction={{
            label: "Discard changes",
            onClick: handleReset,
          }}
        >
          You have unsaved changes in the email template. They&apos;ll be lost if you close the email template editor
          without saving them.
        </ConfirmActionModal>
      )}
    </FullPageViewContainer>
  );
};

export const TemplateComposer: FC = () => {
  const history = useHistory();

  return (
    <ErrorBoundary onError={() => history.push(SettingsRoutes.EmailTemplates)}>
      <Component />
    </ErrorBoundary>
  );
};
